import { FiltradorDeErros } from 'src/erros'
import { obterSecretAsync } from 'src/typescript/servicos/bifrost/bifrost.servico'
import i18n from 'src/typescript/servicos/i18n/i18n.servico'
import { modalDeErro } from 'src/utils'

const QUICKHUNT_SCRIPT_ID = 'pontotel-quickhunt-script'
const QUICKHUNT_SCRIPT_SRC = 'https://widget.quickhunt.app/widgetScript.js'
const QUICKHUNT_READY_TIMEOUT_MS = 60000

type QuickhuntConfig = {
  Quickhunt_Widget_Key: string
}

type Quickhunt = (chave: string) => void

declare global {
  interface Window {
    Quickhunt?: Quickhunt
    Quickhunt_Config?: QuickhuntConfig[]
  }
}

let quickhuntScriptPromise: Promise<Quickhunt> | null = null
let chaveQuickhuntPromise: Promise<string> | null = null

function configurarQuickhunt (chave: string): void {
  window.Quickhunt_Config = [{ Quickhunt_Widget_Key: chave }]
}

async function obterChaveQuickhunt (): Promise<string> {
  if (!chaveQuickhuntPromise) {
    chaveQuickhuntPromise = obterSecretAsync('QUICKHUNT_WIDGET_KEY')
      .then((chaveQuickhunt) => {
        if (!chaveQuickhunt) {
          throw new Error('Chave QUICKHUNT_WIDGET_KEY nao encontrada.')
        }

        return chaveQuickhunt
      })
      .catch((erro) => {
        chaveQuickhuntPromise = null
        throw erro
      })
  }

  return chaveQuickhuntPromise
}

function aguardarQuickhuntDisponivel (): Promise<Quickhunt> {
  return new Promise((resolve, reject) => {
    const iniciadoEm = Date.now()

    const verificarDisponibilidade = () => {
      if (window.Quickhunt) {
        resolve(window.Quickhunt)
        return
      }

      if ((Date.now() - iniciadoEm) >= QUICKHUNT_READY_TIMEOUT_MS) {
        reject(new Error('Nao foi possivel inicializar o Quickhunt.'))
        return
      }

      window.setTimeout(verificarDisponibilidade, 50)
    }

    verificarDisponibilidade()
  })
}

function carregarScriptQuickhunt (): Promise<Quickhunt> {
  if (window.Quickhunt) {
    return Promise.resolve(window.Quickhunt)
  }

  if (!quickhuntScriptPromise) {
    quickhuntScriptPromise = new Promise<Quickhunt>((resolve, reject) => {
      const scriptJaExiste = document.getElementById(QUICKHUNT_SCRIPT_ID)

      if (scriptJaExiste) {
        const concluirQuandoDisponivel = () => {
          aguardarQuickhuntDisponivel().then(resolve).catch(reject)
        }

        scriptJaExiste.addEventListener('load', concluirQuandoDisponivel, { once: true })
        scriptJaExiste.addEventListener('error', () => reject(new Error('Nao foi possivel carregar o Quickhunt.')), { once: true })
        concluirQuandoDisponivel()
        return
      }

      const scriptQuickhunt = document.createElement('script')
      scriptQuickhunt.id = QUICKHUNT_SCRIPT_ID
      scriptQuickhunt.async = true
      scriptQuickhunt.defer = true
      scriptQuickhunt.src = QUICKHUNT_SCRIPT_SRC
      scriptQuickhunt.onload = () => {
        aguardarQuickhuntDisponivel().then(resolve).catch(reject)
      }
      scriptQuickhunt.onerror = () => reject(new Error('Nao foi possivel carregar o Quickhunt.'))
      document.head.appendChild(scriptQuickhunt)
    }).catch((erro) => {
      quickhuntScriptPromise = null
      throw erro
    })
  }

  return quickhuntScriptPromise as Promise<Quickhunt>
}

export async function abrirWidgetQuickhunt (): Promise<void> {
  try {
    const chaveQuickhunt = await obterChaveQuickhunt()

    configurarQuickhunt(chaveQuickhunt)
    const quickhunt = await carregarScriptQuickhunt()

    if (!quickhunt) {
      throw new Error('Quickhunt nao esta disponivel.')
    }

    quickhunt(chaveQuickhunt)
  } catch (erro) {
    FiltradorDeErros.capturarErro(erro as Error)
    modalDeErro(i18n.t('typescript.servicos.quickhunt.erroAbrirAba').toString())
  }
}
